import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { NavigationCancel, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, fromEvent, Subscription, take } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf } from '@angular/common';

import { AppWizardService } from './offer/services/app-wizard.service';
import { pageTransitions } from './shared/animations/page-transitions';
import { User } from './shared/models/user';
import { AnalyticsService } from './shared/services/analytics.service';
import { DbAnalyticsService } from './shared/services/db-analytics.service';
import { ConnectionService } from './shared/services/connection.service';
import { IconService } from './shared/services/icon.service';
import { TranslationService } from './shared/services/translation.service';
import { UserService } from './shared/services/user.service';
import {
	ProductCharacteristicsTranslationService
} from './shared/services/product-characteristics-translation.services';
import { LoadingSpinnerComponent } from './shared/components/utils/loading-spinner.component';

@Component({
	selector: 'copo-app',
	templateUrl: './app.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,

	imports: [NgIf, MatIconModule, TranslateModule, MatButtonModule, LoadingSpinnerComponent, RouterOutlet]
})
export class AppComponent implements OnDestroy {
	@HostBinding('class.mat-typography') readonly matTypography = true;
	@HostBinding('class.passthrough') readonly passthrough = true;

	firstRouterEventSub: Subscription;
	routerEventSub: Subscription;
	navigationDone = false;

	hasConnection = navigator.onLine;

	connectSub: Subscription;
	disconnectSub: Subscription;

	constructor(
		readonly analyticsService: AnalyticsService,
		readonly userService: UserService,
		readonly router: Router,
		readonly connectionService: ConnectionService,
		readonly translationService: TranslationService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		readonly iconService: IconService,
		private readonly appWizardService: AppWizardService,
		private readonly dbAnalyticsService: DbAnalyticsService
	) {
		iconService.registerAllIcons();
		this.appWizardService.actionToGoBack();
		this.connectSub = fromEvent(globalThis, 'online').subscribe(() => {
			this.hasConnection = navigator.onLine;
		});
		this.disconnectSub = fromEvent(globalThis, 'offline').subscribe(() => {
			this.hasConnection = navigator.onLine;
		});
		this.routerEventSub = this.router.events
			.pipe(
				filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd),
			).subscribe(ev => {
				if (!ev.url.startsWith('/redirect?code')) { // exclude redirect from login
					this.analyticsService.trackPage(ev.url); // APP INSIGHTS
				}
			});

		this.firstRouterEventSub = this.router.events
			.pipe(
				filter((ev): ev is NavigationEnd | NavigationCancel =>
					ev instanceof NavigationEnd || ev instanceof NavigationCancel),
				take(1)
			)
			.subscribe(() => {
				this.navigationDone = true;
			});

		this.connectionService.connectedTasks.push(this.connectedTask);
		this.connectionService.disconnectedTasks.push(this.disconnectedTask);
	}

	@HostBinding('attr.aria-busy') get ariaBusy() {
		return (
			!this.navigationDone
			|| this.connectionService.tokenLoading
			|| this.connectionService.awaitUserData
			|| this.connectionService.windowDisplayed
			|| this.connectionService.checkingConnection
		);
	}

	async login() {
		return this.userService.updateUserData();
	}

	// eslint-disable-next-line @typescript-eslint/require-await
	connectedTask = async (user: User) => {
		// tell to Analytics that a user log in
		this.analyticsService.setUserContext(user);
		this.dbAnalyticsService.setUserContext(user);
		this.analyticsService.trackEvent('login'); // AppInSights
	};

	// eslint-disable-next-line @typescript-eslint/require-await
	disconnectedTask = async () => {
		// tell to Analytics that user logout
		this.analyticsService.setUserContext();
		this.dbAnalyticsService.setUserContext();
		this.analyticsService.trackEvent('logout'); // AppInSights
	};

	ngOnDestroy() {
		this.firstRouterEventSub.unsubscribe();
		this.routerEventSub.unsubscribe();
		this.connectSub.unsubscribe();
		this.disconnectSub.unsubscribe();
		const conIdx = this.connectionService.connectedTasks.indexOf(this.connectedTask);
		this.connectionService.connectedTasks.splice(conIdx, 1);
		const disconIdx = this.connectionService.disconnectedTasks.indexOf(this.disconnectedTask);
		this.connectionService.disconnectedTasks.splice(disconIdx, 1);
	}
}
